import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { Select, type SelectProps } from "../../atoms/select";
import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> &
  Omit<SelectProps, "state" | "value" | "onValueChange" | "id"> & {
    /** Called with the picked value after the form's value changes */
    onValueChange?: SelectProps["onValueChange"];
    /** The trigger's element ID, which the label points at. Defaults to a generated one. */
    id?: string;
  };

export const FormSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  onValueChange,
  id: idProp,
  ...selectProps
}: FormSelectProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-select", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        <Label
          htmlFor={id}
          required={required}
          disabled={selectProps.disabled}
          // The label follows the field (Figma 8063:9447)
          size={selectProps.size}
        >
          {label}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;
          return (
            <>
              <Select
                {...selectProps}
                // Native ignores the Label's htmlFor, so the trigger carries the label
                accessibilityLabel={selectProps.accessibilityLabel ?? label}
                id={id}
                testID={id}
                state={hasError ? "error" : "default"}
                value={field.value ?? ""}
                onValueChange={(value) => {
                  field.onChange(value);
                  onValueChange?.(value);
                }}
              />
              <FormFieldMessage error={displayError} helperText={helperText} />
            </>
          );
        }}
      />
    </Box>
  );
};

FormSelect.displayName = "FormSelect";
